<template>
  <div class="d-flex flex-column align-items-center">
    <div
      v-if="crawlerTask?.status_string !== 'success' && crawlerTask?.status_string !== 'error' && !finished"
      class="text-center py-5">
      <div class="spinner-border text-primary mb-3" role="status" style="width: 2.5rem; height: 2.5rem">
        <span class="visually-hidden">Loading...</span>
      </div>
      <h5 class="mb-2 text-muted">{{ $t('order.job_posting.currently_crawling') }}</h5>
      <p class="text-secondary small mb-0" v-if="crawlerTask">
        {{ $t(crawlerTaskStatuses[crawlerTask.status_string]) }}
      </p>
    </div>
    <div v-else class="w-100">
      <div v-if="crawlerTask && crawlerTask.status_string === 'error'" class="alert alert-warning mx-4" role="alert">
        <h4>{{ $t('order.job_posting.could_not_crawl') }}</h4>
        <p class="text-secondary">
          {{ $t('order.job_posting.could_not_crawl_text') }}
        </p>
        <div class="text-center">
          <button class="btn btn-warning my-2" @click="retryCrawl">{{ $t('order.job_posting.retry_btn') }}</button>
        </div>
      </div>
      <div class="w-100 ms-4">
        <div v-once :id="editorId" @keydown="handleKeyDown"></div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { getMetaDocument, retryGetMetaDocument, updateMetaDocument } from '@/api/meta';
import translator from '@/locale/translator';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import EditorjsList from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

class CustomList extends EditorjsList {
  override renderSettings() {
    return super.renderSettings().filter((item) =>
      // Here you can filter needed items
      // In my case I need to only support unordered and ordered lists without any new options like 'checklist' or 'start from'
      // But if you want to only disable Checklist you can filter like (item) => item.label !== 'Checklist'
      ['Unordered', 'Ordered'].includes((item as { label: string }).label)
    );
  }
}

const props = defineProps<{
  orderId: string;
}>();

const crawlerTaskStatuses = {
  waiting: 'order.job_posting.crawler_status.waiting',
  in_progress: 'order.job_posting.crawler_status.in_progress',
  success: 'order.job_posting.crawler_status.success',
  error: 'order.job_posting.crawler_status.error',
  blocked: 'order.job_posting.crawler_status.blocked',
  blocked_by_subflow: 'order.job_posting.crawler_status.blocked_by_subflow',
  exec_canceled: 'order.job_posting.crawler_status.exec_canceled',
  postpone_scheduled: 'order.job_posting.crawler_status.postpone_scheduled',
  postponed: 'order.job_posting.crawler_status.postponed',
  unknown: 'order.job_posting.crawler_status.unknown',
};

const router = useRouter();
const toast = useToast();
const editorData = ref([]);
const h1Block = ref<any>(null);
const crawlerTask = ref();
const finished = ref(false);

const emit = defineEmits<{
  (e: 'didUpdate', data: any): void;
}>();

// We'll store a reference to the Editor.js instance
let editor: EditorJS | null = null;
let pollingInterval: ReturnType<typeof setInterval> | null = null;
const POLLING_INTERVAL_MS = 5000;

/**
 * Generate a unique ID to use as the Editor.js holder.
 * In practice, you can pass this in as a prop or generate it however you like.
 */
const editorId = `editorjs-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Initializes the EditorJS instance.
 */
const initializeEditor = () => {
  if (editor) return; // Prevent re-initialization
  // Double-check if the holder element exists before initializing
  const holderElement = document.getElementById(editorId);
  if (!holderElement) {
    console.error('Failed to find the editor container element.');
    return;
  }

  try {
    editor = new EditorJS({
      holder: editorId,
      // because we are initializing the editor with a reactive variable, we need to pass a copy of the data to prevent the parsing issues that we were encountering (like not displaying lists)
      // known issue: https://github.com/editor-js/list/issues/142
      data: { blocks: JSON.parse(JSON.stringify(editorData.value)) as any },
      onChange: async () => {
        if (!editor) return;
        const newData = await editor.save();
        const blocksToUpdate = [...newData.blocks];
        // if we had an h1 block, add it back before saving the publication
        if (h1Block.value) {
          blocksToUpdate.unshift(h1Block.value);
        }
        updateMetaDocument(props.orderId, blocksToUpdate)
          .then(() => {
            // toast.success('Document saved successfully');
            emit('didUpdate', blocksToUpdate);
          })
          .catch((error: any) => {
            toast.error(error.responseJson?.message ?? error.message);
          });
      },
      tools: {
        header: {
          class: Header as any,
          inlineToolbar: true,
          config: {
            levels: [2, 3, 4, 5, 6],
            defaultLevel: 2,
            placeholder: translator.get('order.job_posting.placeholder'),
          },
        },
        list: {
          class: CustomList as any,
          inlineToolbar: true,
          toolbox: [{ data: { style: 'ordered' } }, { data: { style: 'unordered' } }],
        },
        paragraph: {
          class: Paragraph as any,
          inlineToolbar: true,
          config: { placeholder: translator.get('order.job_posting.placeholder'), preserveBlank: true },
        },
      },
    });
  } catch (err) {
    console.error('Error initializing Editor.js:', err);
    toast.error('Failed to initialize the editor.');
  }
};

onMounted(() => {
  fetchJobPosting();
});
/**
 * If this component can be destroyed, ensure Editor.js is cleaned up.
 */
onBeforeUnmount(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  if (editor && typeof editor.destroy === 'function') {
    editor.destroy();
  }
  editor = null;
});

/* --------------------------------- METHODS -------------------------------- */
/**
 * Fetches the job posting data and task status.
 */
async function fetchJobPosting() {
  try {
    const res = await getMetaDocument(props.orderId);
    const rawData = res.data?.length ? res.data : [];
    // Check if the first block is an H1 header and separate it from the rest of the content (POPS will strip the h1 header and wont display it)
    if (rawData.length > 0 && rawData[0].type === 'header' && rawData[0].data.level === 1) {
      h1Block.value = rawData[0];
      editorData.value = rawData.slice(1);
    } else {
      h1Block.value = null;
      editorData.value = rawData;
    }
    // Sometimes res.data has data and res.task is null
    if (editorData.value.length > 0 && res.task === null) crawlerTask.value = { status_string: 'success' };
    else crawlerTask.value = res.task || null;

    if (
      !crawlerTask.value ||
      crawlerTask.value?.status_string === 'success' ||
      crawlerTask.value?.status_string === 'error'
    ) {
      // We put setTimeout to allow UI to update before initializing the editor
      setTimeout(() => {
        finished.value = true;
      }, 200);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
      emit('didUpdate', res.data);
    } else if (!pollingInterval) {
      // If crawling is in progress and polling hasn't started, start it
      pollingInterval = setInterval(fetchJobPosting, POLLING_INTERVAL_MS);
    }
  } catch (error: any) {
    console.error('Request failed:', error);

    if ([401, 403, 404].includes(error.responseJson?.status)) router.replace({ name: 'orders.list' });

    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  }
}

function retryCrawl() {
  retryGetMetaDocument(props.orderId)
    .then(() => {
      toast.info(translator.get('order.job_posting.retrying'), { timeout: 1500 });
      // Reset state to start polling again
      crawlerTask.value = { status_string: 'waiting' };
      finished.value = false;
      if (!pollingInterval) {
        pollingInterval = setInterval(fetchJobPosting, POLLING_INTERVAL_MS);
      }
    })
    .catch((error: any) => {
      console.log('Retry crawl request failed:', error);
    });
}
function handleKeyDown(event: any) {
  // Check for Shift + Enter
  if (event.shiftKey && event.key === 'Enter') {
    event.preventDefault(); // Prevent default behavior (new div in Safari)

    // Get the current selection
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    // Insert a newline character
    const newline = document.createElement('br');
    range.insertNode(newline);

    // Move the cursor after the newline
    range.setStartAfter(newline);
    range.setEndAfter(newline);
    selection.removeAllRanges();
    selection.addRange(range);

    // Notify Editor.js of the change (optional, depending on your setup)
    editor!.saver.save();
  }
}

watch(
  finished,
  (value) => {
    if (value) {
      initializeEditor();
    }
  },
  { flush: 'post' }
);
</script>
<style>
.ce-block .ce-block__content {
  margin-left: 60px;
}

.ce-toolbar__content {
  margin: 0 0 0 6px;
}

.ce-toolbar__content .ce-toolbar__actions {
  left: -3px;
  top: 2px;
  right: auto;
}
</style>
